import React from 'react';
import Button from '../../../../components/ui/Button';
import {
  X,
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle,
  User
} from 'lucide-react';

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm text-foreground">{value ?? 'N/A'}</p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const parsed = new Date(dateString);
  return Number.isNaN(parsed.getTime()) ? dateString : parsed.toLocaleDateString();
};

const AttendanceDetailsModal = ({ isOpen, onClose, attendance, isLoading, error }) => {
  if (!isOpen) return null;

  const attendanceData = attendance?.attendance_data || {};

  const statusBadge = (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        attendanceData?.status === 'present'
          ? 'bg-green-100 text-green-700'
          : attendanceData?.status === 'absent'
          ? 'bg-red-100 text-red-700'
          : attendanceData?.status === 'late'
          ? 'bg-yellow-100 text-yellow-700'
          : attendanceData?.status === 'half_day'
          ? 'bg-orange-100 text-orange-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {attendanceData?.status ? attendanceData.status.charAt(0).toUpperCase() + attendanceData.status.slice(1).replace('_', ' ') : 'N/A'}
    </span>
  );

  const approvalStatusBadge = attendance?.approval_status && (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        attendance.approval_status === 'approved'
          ? 'bg-green-100 text-green-700'
          : attendance.approval_status === 'rejected'
          ? 'bg-red-100 text-red-700'
          : 'bg-yellow-100 text-yellow-700'
      }`}
    >
      {attendance.approval_status.charAt(0).toUpperCase() + attendance.approval_status.slice(1)}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Attendance Details</p>
                <h2 className="text-xl font-semibold text-foreground">
                  {attendance?.employee_name || 'Attendance'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {formatDate(attendance?.date)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Created {formatDateTime(attendance?.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Updated {formatDateTime(attendance?.updated_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {statusBadge}
            {approvalStatusBadge}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading attendance details...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Employee Information
                  </h3>
                  <DetailField label="Employee Name" value={attendance?.employee_name} />
                  <DetailField label="Employee ID" value={attendance?.employee} />
                  <DetailField label="Date" value={formatDate(attendance?.date)} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Attendance Information
                  </h3>
                  <DetailField label="Status" value={attendanceData?.status ? attendanceData.status.charAt(0).toUpperCase() + attendanceData.status.slice(1).replace('_', ' ') : 'N/A'} />
                  <DetailField label="Check In Time" value={attendanceData?.check_in_time || 'N/A'} />
                  <DetailField label="Check Out Time" value={attendanceData?.check_out_time || 'N/A'} />
                  {attendanceData?.working_hours && (
                    <DetailField label="Working Hours" value={`${attendanceData.working_hours} hours`} />
                  )}
                </div>
              </section>

              {attendanceData?.remarks && (
                <section className="p-4 border border-gray-200 rounded-lg space-y-4">
                  <h3 className="text-sm font-semibold text-foreground">Remarks</h3>
                  <p className="text-sm text-foreground">{attendanceData.remarks}</p>
                </section>
              )}

              {attendance?.approval_status && (
                <section className="p-4 border border-gray-200 rounded-lg space-y-4">
                  <h3 className="text-sm font-semibold text-foreground">Approval Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DetailField label="Approval Status" value={attendance.approval_status ? attendance.approval_status.charAt(0).toUpperCase() + attendance.approval_status.slice(1) : 'N/A'} />
                    <DetailField label="Approved By" value={attendance?.approved_by_name || attendance?.approved_by || 'N/A'} />
                    <DetailField label="Approved At" value={formatDateTime(attendance?.approved_at)} />
                    {attendance?.approval_notes && (
                      <DetailField label="Approval Notes" value={attendance.approval_notes} />
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceDetailsModal;

